/**
 * @description Products workspace data: products, categories, vendors, and discounts.
 * @dependencies AuthContext, products/categories/vendors/discounts API services.
 * @notes Discount writes stay on `/api/discounts`. Company scope is X-Company-Id from apiClient.
 */

import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../contexts/AuthContext';
import { productsService, categoriesService, vendorsService, discountsService } from '../services/api';
import { getApiErrorMessage } from '../utils/apiErrorMessage';

export const useProducts = () => {
 const { user } = useUser();
 const [loading, setLoading] = useState(true);
 const [error, setError] = useState(null);

 // Data state
 const [products, setProducts] = useState([]);
 const [categories, setCategories] = useState([]);
 const [vendors, setVendors] = useState([]);
 const [discounts, setDiscounts] = useState([]);

 // Fetch all data
 const fetchData = useCallback(async () => {
   if (!user?.id) return;

   try {
     setLoading(true);
     setError(null);

     // Fetch all data in parallel (session-based - uses req.user from authentication)
     const [productsData, categoriesData, vendorsData, discountsData] = await Promise.all([
       productsService.getMerchantProducts().catch(() => []),
       categoriesService.getMerchantCategories().catch(() => []),
       vendorsService.getMerchantVendors().catch(() => []),
       discountsService.getDiscounts().catch(() => [])
     ]);

     setProducts(productsData || []);
     setCategories(categoriesData || []);
     setVendors(vendorsData || []);
     setDiscounts(discountsData || []);
   } catch (err) {
     console.error('Failed to fetch data:', err);
     setError('Failed to load data. Please try again.');
   } finally {
     setLoading(false);
   }
 }, [user?.id]);

 // Initial data fetch
 useEffect(() => {
   fetchData();
 }, [fetchData]);

 // Get parent ID helper
 const getParentId = useCallback(() => {
   return user?.id;
 }, [user?.id]);

 // Product operations
 const productOperations = {
   create: async (productData) => {
     try {
       setError(null);
       const dataWithParent = {
         ...productData,
         parentId: getParentId(),
         price: parseFloat(productData.price) || 0,
         stockQuantity: parseInt(productData.stockQuantity) || 0
       };
       
       await productsService.createProduct(dataWithParent);
       await fetchData(); // Refresh all data
     } catch (err) {
       console.error('Failed to create product:', err);
       setError('Failed to create product. Please try again.');
       throw err;
     }
   },

   update: async (id, productData, { refetch: shouldRefetch = true } = {}) => {
    try {
      setError(null);
      const dataWithParent = {
        ...productData,
        parentId: getParentId(),
        price: parseFloat(productData.price) || 0,
        stockQuantity: parseInt(productData.stockQuantity) || 0
      };
      
      await productsService.updateProduct(id, dataWithParent);
      if (shouldRefetch) {
        await fetchData();
      }
    } catch (err) {
      console.error('Failed to update product:', err);
      setError('Failed to update product. Please try again.');
      throw err;
    }
  },

   delete: async (id) => {
     try {
       setError(null);
       await productsService.deleteProduct(id);
       await fetchData();
     } catch (err) {
       console.error('Failed to delete product:', err);
       setError('Failed to delete product. Please try again.');
       throw err;
     }
   },

   getDetails: async (id) => {
     try {
       const [product, variants] = await Promise.all([
         productsService.getProduct(id),
         productsService.getVariants(id).catch(() => [])
       ]);
       return { ...product, variants: variants || [] };
     } catch (err) {
       console.error('Failed to get product details:', err);
       throw err;
     }
   }
 };

 // Variant operations
 const variantOperations = {
   create: async (productId, variantData) => {
     try {
       setError(null);
       await productsService.createVariant(productId, variantData);
     } catch (err) {
       console.error('Failed to create variant:', err);
       setError('Failed to create variant. Please try again.');
       throw err;
     }
   },

   update: async (productId, variantId, variantData) => {
     try {
       setError(null);
       await productsService.updateVariant(productId, variantId, variantData);
     } catch (err) {
       console.error('Failed to update variant:', err);
       setError('Failed to update variant. Please try again.');
       throw err;
     }
   },

   delete: async (productId, variantId) => {
     try {
       setError(null);
       await productsService.deleteVariant(productId, variantId);
     } catch (err) {
       console.error('Failed to delete variant:', err);
       setError('Failed to delete variant. Please try again.');
       throw err;
     }
   }
 };

 // Category operations
 const categoryOperations = {
   create: async (categoryData) => {
     try {
       setError(null);
       const dataWithParent = {
         ...categoryData,
         parentId: getParentId()
       };
       
       await categoriesService.createCategory(dataWithParent);
       await fetchData();
     } catch (err) {
       console.error('Failed to create category:', err);
       setError('Failed to create category. Please try again.');
       throw err;
     }
   },

   delete: async (id) => {
     try {
       setError(null);
       await categoriesService.deleteCategory(id);
       await fetchData();
     } catch (err) {
       console.error('Failed to delete category:', err);
       setError('Failed to delete category. Please try again.');
       throw err;
     }
   }
 };

 // Vendor operations
 const vendorOperations = {
   create: async (vendorData) => {
     try {
       setError(null);
       const dataWithParent = {
         ...vendorData,
         parentId: getParentId()
       };
       
       await vendorsService.createVendor(dataWithParent);
       await fetchData();
     } catch (err) {
       console.error('Failed to create vendor:', err);
       setError('Failed to create vendor. Please try again.');
       throw err;
     }
   },

   update: async (id, vendorData) => {
     try {
       setError(null);
       const dataWithParent = {
         ...vendorData,
         parentId: getParentId()
       };
       
       await vendorsService.updateVendor(id, dataWithParent);
       await fetchData();
     } catch (err) {
       console.error('Failed to update vendor:', err);
       setError('Failed to update vendor. Please try again.');
       throw err;
     }
   },

   delete: async (id) => {
     try {
       setError(null);
       await vendorsService.deleteVendor(id);
       await fetchData();
     } catch (err) {
       console.error('Failed to delete vendor:', err);
       setError('Failed to delete vendor. Please try again.');
       throw err;
     }
   }
 };

 const discountOperations = {
   create: async (discountData) => {
     try {
       setError(null);
       await discountsService.createDiscount(discountData);
       await fetchData();
     } catch (err) {
       console.error('Failed to create discount:', err);
       setError(getApiErrorMessage(err, 'Failed to create discount. Please try again.'));
       throw err;
     }
   },

   update: async (id, discountData) => {
     try {
       setError(null);
       await discountsService.updateDiscount(id, discountData);
       await fetchData();
     } catch (err) {
       console.error('Failed to update discount:', err);
       setError(getApiErrorMessage(err, 'Failed to update discount. Please try again.'));
       throw err;
     }
   },

   delete: async (id) => {
     try {
       setError(null);
       await discountsService.deleteDiscount(id);
       await fetchData();
     } catch (err) {
       console.error('Failed to delete discount:', err);
       setError(getApiErrorMessage(err, 'Failed to remove discount. Please try again.'));
       throw err;
     }
   },

   getDetails: async (id) => {
     try {
       return await discountsService.getDiscount(id);
     } catch (err) {
       console.error('Failed to get discount details:', err);
       throw err;
     }
   }
 };

 return {
   // Data
   products,
   categories,
   vendors,
   discounts,
   
   // State
   loading,
   error,
   setError,
   
   // Operations
   productOperations,
   variantOperations,
   categoryOperations,
   vendorOperations,
   discountOperations,
   
   // Helpers
   refetch: fetchData
 };
};